import React, {PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {getCurrentUnscheduledTasks, getSolutionUnscheduledTasks} from '../services/schedule.api'
import {getValueAsLoadable, LoadableValue, LOADING} from '../../../../utils/loadingValue'
import {compose} from 'redux'
import {injectTypeTacheValueList} from '../../../../modules/common/valueLists/valueListInjectors'
import {mapUnscheduledTaskApiToApp} from '../utils/ScheduleApiUtils'
import {UnscheduledTask} from '../services/schedule.types'
import {createContextWithoutDefault} from '../../../../utils/hooks/context'
import {useScheduler} from './SchedulerContext'
import {ValueListEntry} from '../../../../utils/types/global.types'

export const UnscheduledTasksFormName = 'UnscheduledTasksForm'

export type UnscheduledTasksFormValues = {
	query?: string
}

const pageSize = 20

type UnscheduledTasks = {
	unscheduledTasks: LoadableValue<UnscheduledTask[]>,
	nbUnscheduledTasks: number,
	loadMoreUnscheduledTasks: () => Promise<void>
	query?: string
	handleQuery: (values: UnscheduledTasksFormValues) => void
	insertUnscheduledTask: (task: UnscheduledTask, index: number) => void
	highlightedTaskId?: string
}

type UnscheduledTasksProviderProps = {
	isResultat: boolean
	typeTacheList: ValueListEntry[]
}

const [UnscheduledTasksContext, useUnscheduledTasks] = createContextWithoutDefault<UnscheduledTasks>('UnscheduledTasks')

const UnscheduledTasksProvider = ({ children, isResultat, typeTacheList }: PropsWithChildren<UnscheduledTasksProviderProps>) => {
	const { refreshMarker } = useScheduler()
	const loadingRef = useRef<boolean>(false)
	const [unscheduledTasks, setUnscheduledTasks] = useState<LoadableValue<UnscheduledTask[]>>(LOADING)
	const [nbUnscheduledTasks, setNbUnscheduledTasks] = useState(0)
	const [query, setQuery] = useState<string | undefined>()
	const [highlightedTaskId, setHighlightedTaskId] = useState<string | undefined>()

	const loadPage = async (pageOffset: number, forceReset: boolean, query?: string) => {
		if (loadingRef.current) {
			// Il y a deux chargements qui ont lieu en même temps, on bloque le deuxième
			return Promise.reject()
		}
		loadingRef.current = true
		const promise = isResultat ? getSolutionUnscheduledTasks : getCurrentUnscheduledTasks
		const res = await promise(pageOffset, pageSize, query)
		loadingRef.current = false
		setUnscheduledTasks((prevItems) => {
			if (prevItems.loading || prevItems.error || forceReset) {
				return getValueAsLoadable(mapUnscheduledTaskApiToApp(typeTacheList, res.content))
			} else {
				return getValueAsLoadable([...prevItems.value, ...mapUnscheduledTaskApiToApp(typeTacheList, res.content)])
			}
		})
		setNbUnscheduledTasks(Number.parseInt(res.totalElements))
	}

	const loadMoreUnscheduledTasks = async () => {
		if (unscheduledTasks.loading || unscheduledTasks.error) {
			return
		}
		return await loadPage(Math.floor(unscheduledTasks.value.length / pageSize), false, query)
	}

	// On charge la première page immédiatement afin de connaitre le nombre d'item
	useEffect(() => {
		setNbUnscheduledTasks(0)
		loadPage(0, true, query)
	}, [query, refreshMarker])

	const handleQuery = useCallback((values: UnscheduledTasksFormValues) => {
		if (query !== values.query) {
			setUnscheduledTasks(LOADING)
			setQuery(values.query)
		}
	}, [query, setQuery, setUnscheduledTasks])

	// Insertion client d'une tâche déplanifiée à la position du drop (sans refetch), avec surbrillance transitoire
	const insertUnscheduledTask = useCallback((task: UnscheduledTask, index: number) => {
		setUnscheduledTasks(prev => {
			if (prev.loading || prev.error) {
				return getValueAsLoadable([task])
			}
			const items = [...prev.value]
			const insertIndex = Math.max(0, Math.min(index, items.length))
			items.splice(insertIndex, 0, task)
			return getValueAsLoadable(items)
		})
		setNbUnscheduledTasks(nb => nb + 1)
		setHighlightedTaskId(task.id)
	}, [])

	const contextValue = useMemo(() => ({
		unscheduledTasks,
		nbUnscheduledTasks: nbUnscheduledTasks,
		loadMoreUnscheduledTasks,
		query,
		handleQuery,
		insertUnscheduledTask,
		highlightedTaskId
	}), [unscheduledTasks, nbUnscheduledTasks, loadMoreUnscheduledTasks, query, handleQuery, insertUnscheduledTask, highlightedTaskId])

	return (
		<UnscheduledTasksContext.Provider value={contextValue}>
			{children}
		</UnscheduledTasksContext.Provider>
	)
}

const ConnectedUnscheduledTasksProvider = compose(
	injectTypeTacheValueList
)(UnscheduledTasksProvider)


export {
	ConnectedUnscheduledTasksProvider,
	useUnscheduledTasks
}

